/**
 * Card de uma assinatura-padrão com LR individual (EW.F3.T08 + E13.F7c.T01). // ux-neutrality:allow doc-comment
 *
 * E13.F7c.T01: fallback 'Padrão N' quando signature.label ausente; UUID em // ux-neutrality:allow doc-comment
 * <details>; LR via LRPill primitive (F1). E27.F1 removeu o menu kebab: // ux-neutrality:allow doc-comment
 * Substituir/Remover eram placeholders e Visualizar duplicava o clique no // ux-neutrality:allow doc-comment
 * thumbnail (onSelect). // ux-neutrality:allow doc-comment
 */
import { Blueprint } from '@/components/ui/Blueprint';
import { LRPill } from '@/components/ui/LRPill';
import { MonoData } from '@/components/ui/MonoData';
import { SpecimenFrame } from '@/components/ui/SpecimenFrame';
import { ptBR } from '@/i18n/pt-BR';
import type { SignatureRecordData } from '@/lib/case_signatures';
import type { PatternAnalysisData } from '@/lib/case_workspace';
import { cn } from '@/lib/utils';
import { useSignatureBlob } from './useSignatureBlob';

export interface PatternCardProps {
  signature: SignatureRecordData;
  patternResult: PatternAnalysisData | null;
  caseId: string;
  onSelect?: () => void;
  selected?: boolean;
  /** Índice 0-based usado no fallback "Padrão N" quando signature.label estiver vazio. */
  index?: number;
}

function resolveLabel(signature: SignatureRecordData, index?: number): string {
  const trimmed = signature.label?.trim();
  if (trimmed && trimmed.length > 0) return trimmed;
  if (index !== undefined) return `${ptBR.workspaceCaso.patternCardFallback} ${index + 1}`;
  return signature.id;
}

export function PatternCard({
  signature,
  patternResult,
  caseId,
  onSelect,
  selected,
  index,
}: PatternCardProps): JSX.Element {
  const blobUrl = useSignatureBlob(caseId, signature.id);
  const label = resolveLabel(signature, index);

  return (
    <Blueprint
      as="article"
      className={cn('pattern-card', selected && 'selected')}
      data-testid={`pattern-card-${signature.id}`}
      data-selected={selected ? 'true' : 'false'}
    >
      <button
        type="button"
        onClick={onSelect}
        className="pattern-thumbnail-btn"
        aria-pressed={selected}
      >
        {/* Prancha 2B: a miniatura é ilustração do padrão selecionado, e é onde
            o duotone em aço vive (decisão do owner, 2026-07-28). A prancha de
            TRABALHO do centro fica em tom real — lá o perito examina a tinta. */}
        {blobUrl ? (
          <SpecimenFrame src={blobUrl} alt={label} className="pattern-thumbnail" />
        ) : (
          <div className="pattern-thumbnail animate-pulse bg-fg/[0.06]" aria-hidden="true" />
        )}
      </button>
      <div className="pattern-meta">
        <div className="flex items-center justify-between gap-2">
          <strong data-testid={`pattern-card-label-${signature.id}`}>{label}</strong>
          {patternResult ? (
            <LRPill lr={patternResult.lr_score} data-testid={`pattern-card-lr-${signature.id}`} />
          ) : null}
        </div>
        {signature.extraction_source != null ? (
          <MonoData
            as="p"
            variant="label"
            data-testid={`pattern-card-origem-${signature.id}`}
            className="mt-sc-2 normal-case"
          >
            {ptBR.workspaceCaso.patternCardOrigemLabel}{' '}
            {signature.extraction_source === 'direct_upload'
              ? ptBR.workspaceCaso.patternCardOrigemDireta
              : signature.source_document_filename
                ? signature.source_document_page != null
                  ? `${signature.source_document_filename}, ${ptBR.workspaceCaso.patternCardOrigemPag} ${signature.source_document_page}`
                  : signature.source_document_filename
                : ptBR.workspaceCaso.patternCardOrigemExtraida}
          </MonoData>
        ) : null}
        <details className="mt-sc-2 text-caption text-fg-dim">
          <summary
            data-testid={`pattern-card-uuid-toggle-${signature.id}`}
            className="cursor-pointer select-none underline-offset-4 hover:underline"
          >
            {ptBR.workspaceCaso.patternCardIdToggle}
          </summary>
          <code data-testid={`pattern-card-uuid-${signature.id}`} className="mt-1 block font-mono">
            {signature.id}
          </code>
        </details>
      </div>
    </Blueprint>
  );
}
